import { action, computed } from 'mobx';
import { NumberStore, BooleanStore } from 'ui/base/input';
import { ModalStore } from 'ui/base/modal';

export enum CalendarInputType {
  SINGLE,
  FIRST,
  FINAL,
}

export abstract class CalendarInputStore {
  type: CalendarInputType;

  year: NumberStore;
  month: NumberStore;
  date: NumberStore;

  tempYear: NumberStore;
  tempMonth: NumberStore;
  tempDate: NumberStore;

  viewDecade: NumberStore;
  viewYear: NumberStore;
  viewMonth: NumberStore;

  modeYear: BooleanStore;
  modeMonth: BooleanStore;
  modeDate: BooleanStore;

  modal: ModalStore;

  constructor(type: CalendarInputType, date: Date) {
    this.type = type;

    this.year = new NumberStore(0);
    this.month = new NumberStore(0);
    this.date = new NumberStore(0);

    this.tempYear = new NumberStore(0);
    this.tempMonth = new NumberStore(0);
    this.tempDate = new NumberStore(0);

    this.viewDecade = new NumberStore(0);
    this.viewYear = new NumberStore(0);
    this.viewMonth = new NumberStore(0);

    this.modeYear = new BooleanStore(false);
    this.modeMonth = new BooleanStore(false);
    this.modeDate = new BooleanStore(false);

    this.modal = new ModalStore(false);

    this.setDate(date);
  }

  @action.bound
  setDate(date: Date): void {
    this.year.setValue(date.getFullYear());
    this.month.setValue(date.getMonth());
    this.date.setValue(date.getDate());
    this.tempYear.setValue(date.getFullYear());
    this.tempMonth.setValue(date.getMonth());
    this.tempDate.setValue(date.getDate());
    this.viewDecade.setValue(Math.floor(date.getFullYear() / 10) * 10);
    this.viewYear.setValue(date.getFullYear());
    this.viewMonth.setValue(date.getMonth());
    this.modeYear.setValue(false);
    this.modeMonth.setValue(false);
    this.modeDate.setValue(true);
  }

  @action.bound
  reloadDate(): void {
    this.setDate(this.committedDateObject);
  }

  @action.bound
  commitDate(): void {
    this.setDate(this.tempDateObject);
  }

  @computed get tempDateObject(): Date {
    return new Date(this.tempYear.value,
                    this.tempMonth.value,
                    this.tempDate.value);
  }

  @computed get committedDateObject(): Date {
    return new Date(this.year.value,
                    this.month.value,
                    this.date.value);
  }
}

export class CalendarSingleInputStore extends CalendarInputStore {
  final?: CalendarFinalInputStore;

  constructor(date: Date) {
    super(CalendarInputType.SINGLE, date);
  }
}

export class CalendarFirstInputStore extends CalendarInputStore {
  range: CalendarRangeInputStore;

  constructor(range: CalendarRangeInputStore, date: Date) {
    super(CalendarInputType.FIRST, date);
    this.range = range;
  }

  @computed get final(): CalendarFinalInputStore {
    return this.range.final;
  }
}

export class CalendarFinalInputStore extends CalendarInputStore {
  range: CalendarRangeInputStore;

  constructor(range: CalendarRangeInputStore, date: Date) {
    super(CalendarInputType.FINAL, date);
    this.range = range;
  }

  @computed get first(): CalendarFirstInputStore {
    return this.range.first;
  }
}

export class CalendarRangeInputStore {
  first: CalendarFirstInputStore;
  final: CalendarFinalInputStore;

  constructor(first: Date, final: Date) {
    this.first = new CalendarFirstInputStore(this, first);
    this.final = new CalendarFinalInputStore(this, final);
  }
}
